import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Mail, Lock, User, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!loginForm.email || !loginForm.password) {
      setError("Please fill in all fields");
      return;
    }

    try {
      setIsLoading(true);
      const userData = await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      
      // Master Admin goes directly to Admin Dashboard
      if (userData?.is_master_admin) {
        navigate("/admin");
      } else {
        navigate("/sessions");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      setError("Please fill in all fields");
      return;
    }

    try {
      setIsLoading(true);
      await register(registerForm.name, registerForm.email, registerForm.password);
      toast.success("Account created successfully!");
      navigate("/sessions");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="w-full max-w-md px-4">
        {/* Logo */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center mb-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_5dae0428-c90f-47d3-96c5-04b06478a28c/artifacts/6qaxp7ot_DMS-logo.png" 
              alt="DMS Logo" 
              className="h-14 md:h-16 w-auto"
            />
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold text-heading text-slate-900">Asset Atlas</h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">CBM Book of Knowledge</p>
        </div>

        <Card className="border-slate-200 shadow-lg" data-testid="login-card">
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-heading text-lg md:text-xl text-center">Welcome</CardTitle>
            <CardDescription className="text-center text-sm">
              Sign in to start your customer session
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              {error && (
                <div className="flex items-center gap-2 p-2 mb-3 bg-red-50 text-red-700 rounded-md text-sm">
                  <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
                  {error}
                </div>
              )}

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="login-email" className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                      Email
                    </Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@company.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="h-10"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="login-password" className="flex items-center gap-2 text-sm">
                      <Lock className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                      Password
                    </Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Enter your password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="h-10"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-10 bg-blue-600 hover:bg-blue-700"
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>

                {/* Demo credentials hint */}
                <div className="mt-4 p-2 bg-slate-50 rounded-md">
                  <p className="text-xs text-slate-500 text-center">
                    Demo: <span className="font-medium">demo@industrial.com / demo123</span>
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="register-name" className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                      Full Name
                    </Label>
                    <Input
                      id="register-name"
                      placeholder="John Smith"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      className="h-10"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="register-email" className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                      Email
                    </Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@company.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      className="h-10"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="register-password" className="flex items-center gap-2 text-sm">
                      <Lock className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                      Password
                    </Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="Create a password"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      className="h-10"
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-10 bg-blue-600 hover:bg-blue-700"
                    disabled={isLoading}
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
